/**
 * The invite landing page: `/t/ABC123`.
 *
 * The critical path for a brand-new player. They see the *table* first — whose
 * it is, which game, how many seats are left — and only then are asked for a
 * name and a passkey. The preview is public and carries no cards and no user
 * ids; the join intent is parked in sessionStorage so it survives the ceremony.
 *
 * The wire shape is `TablePreview` from `@cards/shared` and nothing else. This
 * file used to declare its own near-copy of it — `gameName` for the server's
 * `gameId`, and the object itself rather than the `{ preview }` envelope the
 * route actually sends — so every field read as `undefined` and the heading of
 * the first screen a stranger ever sees read "undefined's table", under it
 * "Every seat is taken" at a table with three seats free.
 */
import { useEffect, useState } from 'react'
import type { GameId, TablePreview as TablePreviewData, TablePreviewResponse } from '@cards/shared'
import { api } from '../auth/session'
import { countWord } from '../table/atoms'
import { GAME_TABS, getGameUi } from '../games/registry'
import { CreateAccount } from '../auth/CreateAccount'
import type { Account } from '../auth/session'
import { useSettings } from '../settings/settings'
import { BotIcon } from '../table/atoms'
import { Placemat, SeatRing, TableDeck, type RingPosition, type RingSeat } from './SeatRing'

export type { TablePreviewData }

const INTENT_KEY = 'cards.joinIntent'

export function rememberJoinIntent(code: string): void {
  try {
    sessionStorage.setItem(INTENT_KEY, code)
  } catch {
    /* the code is also in the URL, so this is a convenience, not the mechanism */
  }
}

export function takeJoinIntent(): string | null {
  try {
    const code = sessionStorage.getItem(INTENT_KEY)
    if (code) sessionStorage.removeItem(INTENT_KEY)
    return code
  } catch {
    return null
  }
}

const capitalise = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1)

/** The game's name as the lobby's own tabs write it. The preview carries the
 *  id, which is not a word anybody says out loud. */
export function gameNameOf(id: GameId): string {
  return GAME_TABS.find((t) => t.id === id)?.name ?? String(id)
}

/** The heading, which is the whole of the first impression. A table whose host
 *  has since left still has a code, and naming it beats naming nobody. */
export function headingFor(preview: TablePreviewData): string {
  return preview.hostName ? `${preview.hostName}'s table` : `Table ${preview.code}`
}

/** "500, already playing. Two seats are free." — one sentence, built from
 *  numbers that are actually numbers. */
export function sentenceFor(preview: TablePreviewData): string {
  const free = Math.max(0, preview.seatCount - preview.seatsFilled)
  const where =
    preview.status === 'playing'
      ? ', already playing. '
      : preview.status === 'finished'
        ? ', and the match is over. '
        : '. '
  const seats =
    preview.status === 'finished'
      ? 'You can still sit down for the next one.'
      : free > 0
        ? `${capitalise(countWord(free))} ${free === 1 ? 'seat is' : 'seats are'} free.`
        : 'Every seat is taken — you can watch.'
  return `${gameNameOf(preview.gameId)}${where}${seats}`
}

/** Seats in turn order from the bottom, for a game that plays clockwise: the
 *  next player sits on your left. An anticlockwise game (the Belotes) mirrors
 *  it, as the table itself does (`positionOf` in table/view.ts), so the seat
 *  a stranger sees on their right here is still on their right once they sit. */
const RING: Record<number, readonly RingPosition[]> = {
  2: ['s', 'n'],
  3: ['s', 'w', 'e'],
  4: ['s', 'w', 'n', 'e'],
}
const MIRROR: Record<RingPosition, RingPosition> = { s: 's', n: 'n', w: 'e', e: 'w' }

/**
 * The preview's seats placed round the table from the stranger's point of
 * view: the first free seat is theirs, at the bottom, and lit; the one across
 * from it is their partner's. A full table is drawn from seat one.
 */
export function previewSeats(preview: TablePreviewData): RingSeat[] {
  const n = preview.seats.length
  const ring = RING[n]
  if (!ring) return []
  const order = getGameUi(preview.gameId).rotation === 'clockwise' ? ring : ring.map((p) => MIRROR[p])
  const free = preview.seats.findIndex((s) => s.kind === 'empty')
  const mine = preview.status === 'finished' ? -1 : free
  const from = mine >= 0 ? mine : 0
  return preview.seats.map((seat, i) => {
    const step = (i - from + n) % n
    const position = order[step]!
    const partner = mine >= 0 && n === 4 && step === 2
    const node =
      i === mine ? (
        <Placemat label="Your seat" lit />
      ) : seat.kind === 'empty' ? (
        <Placemat label="Free" />
      ) : (
        <div className="lobby-seat">
          <div className="seat-name">
            {seat.kind === 'bot' ? <BotIcon /> : null}
            {seat.name}
          </div>
          <div className="seat-sub">{[seat.kind === 'bot' ? 'bot' : null, partner ? 'your partner' : null].filter(Boolean).join(', ') || '\u00A0'}</div>
        </div>
      )
    return { key: i, position, node }
  })
}

export function TablePreview({
  code,
  signedIn,
  onJoin,
  fixture,
}: {
  code: string
  signedIn: boolean
  onJoin: (account: Account | null) => void
  /** Supplied by the demo harness in place of the fetch. */
  fixture?: TablePreviewData
}): JSX.Element {
  const [preview, setPreview] = useState<TablePreviewData | null>(fixture ?? null)
  const [error, setError] = useState<string | null>(null)
  const [settings] = useSettings()

  useEffect(() => {
    if (fixture) return
    rememberJoinIntent(code)
    void api<TablePreviewResponse>(`/api/tables/${encodeURIComponent(code)}/preview`)
      // The envelope is the route's, and unwrapping it is this one line. Every
      // other REST reader in the app does the same with its own.
      .then((r) => {
        if (r?.preview) setPreview(r.preview)
        else setError('That table has closed, or the link has expired.')
      })
      // The reason is for the console; the player gets one sentence.
      .catch(() => setError('That table has closed, or the link has expired.'))
  }, [code, fixture])

  return (
    <div className="page">
      <div className="wordmark">
        cards{' '}
        <span className="suit">
          <span className="s-s">♠</span>
          <span className="s-h">♥</span>
          <span className="s-d">♦</span>
          <span className="s-c">♣</span>
        </span>
      </div>

      <section className="live">
        <h2>{preview ? headingFor(preview) : error ? 'No such table' : 'Looking for the table'}</h2>
        <p>{preview ? sentenceFor(preview) : (error ?? 'One moment.')}</p>
      </section>

      {preview ? (
        <section className="felt invite-felt" aria-label="Who is at the table">
          <SeatRing seats={previewSeats(preview)} centre={<TableDeck code={preview.code} deck={settings.deck} />} />
        </section>
      ) : null}

      <div style={{ marginTop: 'auto' }}>
        {/* A table that is not there cannot be sat at, and offering a biometric
            ceremony as the way to reach it is the cruellest possible dead end:
            the player registers an account and arrives nowhere. */}
        {error ? (
          <button
            className="btn btn-primary btn-block"
            style={{ height: 50 }}
            onClick={() => {
              takeJoinIntent()
              location.assign('/')
            }}
          >
            Go to cards
          </button>
        ) : signedIn ? (
          <button
            className="btn btn-primary btn-block"
            style={{ height: 50 }}
            disabled={!preview}
            onClick={() => onJoin(null)}
          >
            Sit down
          </button>
        ) : (
          <CreateAccount
            heading="Take a seat"
            blurb="An account is a passkey — no password, no email. Your device asks for a fingerprint or face, and that is the whole of it."
            cta="Create passkey & sit down"
            onDone={(account) => onJoin(account)}
          />
        )}
      </div>
    </div>
  )
}
